import React, { Dispatch } from 'react';
import { FieldValues, UseFormWatch } from 'react-hook-form';

import { Button, SpinnerOverlay } from '@components/common';
import Dropzone from '@components/common/Dropzone';
import FilesList from '@components/common/FilesList';
import LinkTasksForm from '@components/ModalComponents/TaskForm/LinkTasksForm';

import { Icons } from '@icons/icons';

import { useMutatePortalTaskAttachFiles } from '@hooks/portal/tasks/useMutatePortalTaskAttachFiles';

import { isEmpty } from '@utils/common';

import { IAssetFile } from '@interfaces/assets/asset';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import { acceptedFileTypes, fileErrors, MAX_BYTES, supportsText } from './constants';
import { tabsNames, taskFormFields } from '@components/ModalComponents/TaskForm/constants';

import styles from './Dependencies.module.scss';

interface IDependenciesProps {
	watch: UseFormWatch<FieldValues>;
	isEditMode: boolean;
	isViewMode: boolean;
	setCurrentTab: (value: string) => void;
	uploadedFiles: Array<IAssetFile>;
	setUploadedFiles: Dispatch<React.SetStateAction<Array<IAssetFile>>>;
	onSubmit: () => void;
	projectId?: string;
	taskId?: string;
}

const Dependencies = ({
	isEditMode,
	isViewMode,
	setCurrentTab,
	uploadedFiles,
	setUploadedFiles,
	onSubmit,
	projectId,
	taskId,
	watch,
}: IDependenciesProps) => {
	const {
		createTaskUploadUrlAttachment,
		progress,
	} = useMutatePortalTaskAttachFiles({ setUploadedFiles });

	const handleRemoveFile = (fileId: string) => {
		setUploadedFiles(prevState => prevState.filter((file: IAssetFile) => file.id !== fileId));
	};

	const isLoading = progress !== undefined ? progress !== HUNDRED_PERCENT_LOADING : false;

	const onClickBackHandler = () => {
		setCurrentTab(tabsNames.details);
	};

	const selectedProjectId = (isEditMode || isViewMode) ? projectId : watch(taskFormFields.project)?.value;

	const isAttachmentsLabel = isViewMode ? !isEmpty(uploadedFiles) : true;

	return (
		<>
			<div className={styles.dependenciesContainer}>
				{isLoading && <SpinnerOverlay />}
				<div className={styles.label}>Dependencies</div>
				<div className={styles.linkTasks}>
					<LinkTasksForm
						isViewMode={isViewMode}
						predecessorTaskEndDate={watch(taskFormFields.endDate)}
						predecessorTaskStartDate={watch(taskFormFields.startDate)}
						projectId={selectedProjectId}
						taskId={taskId}
					/>
				</div>
				{isAttachmentsLabel && <div className={styles.label}>Attachments</div>}
				{!isViewMode
					&& <div className={styles.dropzone}>
						<Dropzone
							acceptedFileTypes={acceptedFileTypes}
							createUploadUrl={createTaskUploadUrlAttachment as any}
							errors={fileErrors}
							isMultipleFiles
							isUploadedFilesInfo={false}
							maxSize={MAX_BYTES}
							supportsText={supportsText}
							uploadIcon={Icons.uploadFile}
							uploadedIcon={Icons.uploadedFile}
						/>
					</div>
				}
				<div className={styles.filesList}>
					<FilesList
						files={uploadedFiles}
						handleRemoveFile={handleRemoveFile}
						isDeleteInViewMode={isEditMode}
						isViewMode={isViewMode || isEditMode}
					/>
				</div>
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Button
						onClick={onSubmit}
						text='Save'
					/>
				</div>
			}
		</>
	);
};

export default Dependencies;
